
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RentalsService } from "../../../../../services/rentals.service";
import { HelpersService } from "../../../../../services/helpers.service";
import { BroadcastService } from "../../../../../services/broadcast.service";
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import moment from 'moment';

interface ServiceIndicatorReport {
  productID: number;
  brand: string;
  model: string;
  yearModel: number;
  size: string;
  serialNumber: string;
  identifier: string;
  serviceIndicatorID: number;
  serviceIndicatorName: string;
  mode: string;
  totalPointsAccumulated: number;
  metric: string;
  threshold: number;
  serviceStatus: number;
  component: string | null;
  lastServiceDate: string | null;
}

type StatusFilter = 'all' | 'healthy' | 'approaching' | 'needsService';

@Component({
  selector: 'app-rentals-service-indicator-status-report',  
  templateUrl: './rentals-service-indicator-status-report.component.html',
  styleUrls: ['./rentals-service-indicator-status-report.component.scss'],
  providers: [MessageService]
})
export class RentalsServiceIndicatorStatusReportComponent implements OnInit {
  reportData: ServiceIndicatorReport[] = [];
  loadingData: boolean = false;
  filteredData: ServiceIndicatorReport[] = [];
  searchTerm: string = '';
  sortColumn: string = 'serviceStatus';
  sortDirection: 'asc' | 'desc' = 'asc';
  
  // Unique product identifiers to group by
  uniqueProducts: string[] = [];
  selectedProduct: string | null = null;
  
  // Status filter
  activeStatusFilter: StatusFilter = 'all';
  
  // Summary counts
  totalProducts: number = 0;
  healthyProducts: number = 0;
  approachingServiceProducts: number = 0;
  needsServiceProducts: number = 0;

  showRentalProduct: boolean = false;
  selectedProductEdit : any = null;

  private readonly HEALTHY_THRESHOLD = 75;
  private readonly WARNING_THRESHOLD = 80;
  private readonly CRITICAL_THRESHOLD = 90;
  private readonly SERVICE_DUE_THRESHOLD = 100;

  constructor(
    private rentalsService: RentalsService,
    private helpersService: HelpersService,
    private translate : TranslateService,
    public messageService: MessageService,
    private broadcastService : BroadcastService
  ){

  }

  ngOnInit(): void {
    this.getServiceIndicatorStatusReport();
  }

  getServiceIndicatorStatusReport(refresh = false) {    
    this.loadingData = true;
    this.rentalsService.getServiceIndicatorStatusReport(this.helpersService.getPartnerId()).subscribe((response: any) => {
      this.reportData = response;
      if(!refresh){
        this.filteredData = [...this.reportData];      
        this.extractUniqueProducts();
        this.calculateSummary();
        this.sort(this.sortColumn);
      }else{
        this.filteredData = [...this.reportData];      
        this.extractUniqueProducts();
        this.calculateSummary();
        this.applyFilters();
        this.applySorting();
      }
      this.broadcastService.sendMessage({reloadServiceIndicator : true})
      this.loadingData = false;
    }, (error: any) => {
      this.loadingData = false;
      console.error('Error fetching service indicator status report:', error);
    });
  }

  calculateSummary(): void {
    
    this.totalProducts = this.reportData.length;
        
    // Count products in each category
    this.healthyProducts = 0;
    this.approachingServiceProducts = 0;
    this.needsServiceProducts = 0;

    this.reportData.forEach(item => {
      if (item.serviceStatus < this.HEALTHY_THRESHOLD) {
        this.healthyProducts++;
      } else if (item.serviceStatus < this.SERVICE_DUE_THRESHOLD) {
        this.approachingServiceProducts++;
      } else {
        this.needsServiceProducts++;
      }
    });
  }

  extractUniqueProducts(): void {
    const identifiers = new Set<string>();
    this.reportData.forEach(item => {
      identifiers.add(item.brand + " " + item.model);
    });
    this.uniqueProducts = Array.from(identifiers);
  }

  filterByProduct(identifier: string | null): void {
    this.selectedProduct = identifier;
    this.applyFilters();
  }

  filterByStatus(status: StatusFilter): void {
    this.activeStatusFilter = status;
    this.applyFilters();
  }

  applyFilters(): void {
    // Start with all data
    let filtered = [...this.reportData];
    
    // Apply product filter if selected
    if (this.selectedProduct) {
      filtered = filtered.filter(item => (item.brand + " " + item.model) === this.selectedProduct);
    }
    
    // Apply search term if any
    if (this.searchTerm.trim()) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        return (
          item.brand.toLowerCase().includes(term) ||
          item.model.toLowerCase().includes(term) ||
          item.serialNumber.toLowerCase().includes(term) ||
          item.serviceIndicatorName.toLowerCase().includes(term) ||
          (item.component && item.component.toLowerCase().includes(term))
        );
      });
    }
    
    // Apply status filter if not 'all'
    if (this.activeStatusFilter !== 'all') {
      if (this.activeStatusFilter === 'healthy') {
        filtered = filtered.filter(item => item.serviceStatus < this.HEALTHY_THRESHOLD);
      } else if (this.activeStatusFilter === 'approaching') {
        filtered = filtered.filter(item => item.serviceStatus >= this.HEALTHY_THRESHOLD && item.serviceStatus < this.SERVICE_DUE_THRESHOLD);
      } else if (this.activeStatusFilter === 'needsService') {
        filtered = filtered.filter(item => item.serviceStatus >= this.SERVICE_DUE_THRESHOLD);
      }
    }
    
    this.filteredData = filtered;
    
    // Apply sorting
    this.applySorting();
  }

  search(): void {
    this.applyFilters();
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.applySorting();
  }

  applySorting(): void {
    console.log(this.sortColumn)
    this.filteredData.sort((a: any, b: any) => {
      var valueA = a[this.sortColumn];
      var valueB = b[this.sortColumn];

      if(this.sortColumn === 'lastServiceDate'){
        valueA = valueA != null ? moment(valueA).valueOf() : moment("1970-01-01").valueOf();
        valueB = valueB != null ? moment(valueB).valueOf() : moment("1970-01-01").valueOf();
      }

      if (valueA === null) return this.sortDirection === 'asc' ? 1 : -1;
      if (valueB === null) return this.sortDirection === 'asc' ? -1 : 1;
      
      if (typeof valueA === 'string') {
        return this.sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } else {
        return this.sortDirection === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }
    });
  }

  getStatusClass(status: number): string {
    if (status < this.HEALTHY_THRESHOLD) return 'status-good';
    if (status < this.CRITICAL_THRESHOLD) return 'status-warning';
    if (status < this.SERVICE_DUE_THRESHOLD) return 'status-attention';
    return 'status-critical';
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  getStatusPercentage(status: number): number {
    return Math.min(Math.round(status), 100);
  }

  showProduct(product : any){
    this.selectedProductEdit = { id : product.productID };
    this.showRentalProduct = true;
  }
 
  closeRentalProduct(){
    this.showRentalProduct = false;
    this.getServiceIndicatorStatusReport(true);
  }
  
}